/**
 * info about the Auth HTTP header
 * BE uses this header to control access to GQL operations
 * or specific object (like orders or companies) based on user permissions
 */
export const AUTH_HEADER = {
  name: 'Authorization',
  create: (authToken: string) => `Bearer ${authToken}`,
  // parse the token out of the header value (reverse operation to the one above – keep in sync)
  parseToken: (headerValue: string) => {
    const headerParts = headerValue.split(` `)
    return headerParts.length < 2 ? null : headerParts[1]
  },
}

/**
 * info about the selected language HTTP header
 * this is our custom header that the BE accepts & that can control language of BE texts
 * (eg. ware item translations, labels, error messages etc.)
 * it should be set to the same language that the FE app is rendered with (that the user prefers/selected)
 */
const LANGUAGE_HEADER = {
  name: 'X-User-Language',
  create: (selectedLanguage: string) => selectedLanguage,
}

/**
 * info about the accepted languages HTTP header
 * the value should be a list of locales (cs, en, ...) that the customer prefers/accepts & the FE app supports
 * BE uses this eg. to determine the default user-branch language and available user-branch languages
 * by comparing this with the language list that the requested company (brand/branch) offers
 */
const ACCEPTED_LANGUAGES_HEADER = {
  name: 'Accept-Language',
  create: (acceptedLanguages: string) => acceptedLanguages,
}

/**
 * header specifying what info (parts of the URL) should be to the 'Referrer' header
 * BE uses that header to distinguish between old `doveze` shop app and this new one (while the are both used)
 * (to eg. navigate to the correct app after the online payment or while generating mail links)
 * and needs the referrer to include the sub-pages as well (not just the the base part of the url ending with TLD)
 * hence the 'no-referrer(-only)-when-downgrade(-to-lower-security)` is used
 * more explanations & examples here: https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Referrer-Policy#no-referrer-when-downgrade
 * the header has to be added as a meta tag,
 */
export const REFERRER_HEADER = {
  nameNextConfig: 'Referrer-Policy', //when adding the header via JS, eg. in next.config.js
  name: 'referrer', // when adding the header as meta tag in HTML head, this is the name to be used
  create: () => 'no-referrer-when-downgrade',
}

type TApolloHeadersOptions = {
  locale: Nullable<string>
  acceptedLanguages?: string
  authToken: Nullable<string>
}

/**
 * returns base headers that the GQL request via Apollo should have
 * it helps with setting the user’s authentication token and his preferred language
 */
export const getBaseHttpHeaders = ({
  locale,
  authToken,
  acceptedLanguages,
}: TApolloHeadersOptions) => {
  let headers: Record<string, string> = {}

  if (locale) {
    headers[LANGUAGE_HEADER.name] = LANGUAGE_HEADER.create(locale)
  }

  if (authToken) {
    headers[AUTH_HEADER.name] = AUTH_HEADER.create(authToken)
  }

  if (acceptedLanguages) {
    headers[ACCEPTED_LANGUAGES_HEADER.name] = ACCEPTED_LANGUAGES_HEADER.create(acceptedLanguages)
  }

  return headers
}
